/**
 * The proposal editor's state machine: local quantities, the dirty flag, the
 * save/discard mutations and — the delicate part — the reconciliation between
 * what the user has typed and what the server has since said.
 *
 * Dirty-guard (baseline approach):
 *   The adopt-vs-preserve decision compares local state against the LAST-RECONCILED
 *   BASELINE (what we last seeded from), NOT the fresh server rows. This prevents
 *   a false "server changed" notice when an unedited user has local == baseline but
 *   the server has since changed (local != new server, but user hasn't edited anything).
 *
 *   Two refs are maintained:
 *     - lastSeededKeyRef  — the serverRowsKey string we last adopted
 *     - lastSeededRowsRef — the baseline Map (productId→qty) we last seeded from
 *
 *   On background server-data arrival:
 *     - Same key → no-op.
 *     - New key + shouldAdoptServer(baseline, local) → adopt (seed, update both refs).
 *     - New key + user has edits vs baseline → preserve local, show amber notice.
 *
 *   Post-save: editLinesMutation.onSuccess advances both refs to the saved state
 *   BEFORE triggering refetch, so the post-save refetch sees local == new baseline
 *   → shouldAdoptServer returns true → clean adopt, no false notice.
 *
 *   Descartar: re-seeds from current server data, updates both refs, clears notice.
 *
 *   The reconciliation effect reads the latest local state via localQuantitiesRef
 *   (kept in sync by a small dedicated effect) rather than a setLocalQuantities
 *   functional updater — the adopt/preserve decision and all ref/state writes run
 *   in the plain effect body, so nothing here depends on an updater being called
 *   exactly once.
 *
 * Per-store isolation is the caller's job: ProposalPage remounts the component
 * that calls this hook with key={storeId}, which resets every piece of state
 * below. Without that, edits made in one store would bleed into the next.
 */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {trpc} from '../../trpc'
import {mapOrderError} from '../../lib/orderErrors'
import {
  buildLines,
  computeDirty,
  divergesFromStored,
  seedLocalQuantities,
  shouldAdoptServer,
} from './proposalEditState'
import type {OrderData, ProposalRow} from './proposalViewModel'

type UseProposalEditorInput = {
  storeId: number
  rows: Array<ProposalRow>
  order: OrderData
  isEditable: boolean
  /** The store's per-line floor — rows below it are left out of the save payload. */
  minQuantityPerProduct: number
}

export type ProposalEditor = {
  /** productId → quantity currently shown (may differ from server while editing). */
  localQuantities: Map<number, number>
  /** Any local quantity differs from the current server quantity. */
  dirty: boolean
  /** Server data changed concurrently while the user had unsaved edits. */
  serverChangedWhileDirty: boolean
  /**
   * The screen shows a row the order does not store — the carta gained a
   * product after the proposal was generated. Enables the save and the notice;
   * NOT part of `dirty`, which arms the unsaved-changes guard.
   */
  cartaChanged: boolean
  /** Spanish copy for a failed save, or null. */
  mutationError: string | null
  /** True while a save is on the wire. */
  isSaving: boolean
  /** Live totals from local quantities (they move while editing). */
  localTotalUnits: number
  localTotalRefs: number
  onQuantityChange: (productId: number, quantity: number) => void
  onSave: () => void
  onDiscard: () => void
}

export const useProposalEditor = ({
  storeId,
  rows,
  order,
  isEditable,
  minQuantityPerProduct,
}: UseProposalEditorInput): ProposalEditor => {
  const utils = trpc.useUtils()

  const [localQuantities, setLocalQuantities] = useState<Map<number, number>>(() => new Map())
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [serverChangedWhileDirty, setServerChangedWhileDirty] = useState(false)

  // Stable serialisation of server rows — changes only when server quantities change.
  const serverRowsKey = rows.map((row) => `${row.product.id}:${row.quantity}`).join(',')

  const lastSeededKeyRef = useRef<string>('')
  // Baseline Map: the server quantities that local state was last reconciled to.
  const lastSeededRowsRef = useRef<Map<number, number>>(new Map())

  // Mirrors localQuantities into a ref so the reconciliation effect below can
  // read the latest COMMITTED value synchronously, without adding localQuantities
  // to its dependency array (that would re-run it on every keystroke).
  const localQuantitiesRef = useRef(localQuantities)
  useEffect(() => {
    localQuantitiesRef.current = localQuantities
  }, [localQuantities])

  useEffect(() => {
    const isNewServerData = serverRowsKey !== lastSeededKeyRef.current
    if (!isNewServerData) {
      // Same server data re-arrived (stale refetch no-op) — nothing to do.
      return
    }

    // Decide adopt vs preserve first, purely from the current local state and
    // refs. Only once the decision is made do we write refs / call setState —
    // the setLocalQuantities updater itself is never used for this, so there's
    // no risk of side effects re-running if React invokes an updater twice.
    const currentLocal = localQuantitiesRef.current
    const adopt = shouldAdoptServer(lastSeededRowsRef.current, currentLocal)

    if (adopt) {
      // User has no unsaved edits relative to baseline → adopt new server data.
      const seeded = seedLocalQuantities(rows)
      lastSeededKeyRef.current = serverRowsKey
      lastSeededRowsRef.current = seeded
      setLocalQuantities(seeded)
      setServerChangedWhileDirty(false)
      setMutationError(null)
    } else {
      // User has unsaved edits → preserve local state; raise the notice flag.
      setServerChangedWhileDirty(true)
    }
  }, [serverRowsKey, rows])

  const localTotalUnits = useMemo(
    () => rows.reduce((sum, row) => sum + (localQuantities.get(row.product.id) ?? row.quantity), 0),
    [rows, localQuantities],
  )
  const localTotalRefs = useMemo(
    () => rows.filter((row) => (localQuantities.get(row.product.id) ?? row.quantity) > 0).length,
    [rows, localQuantities],
  )

  // dirty = any local qty differs from current server qty
  // (drives Guardar/Descartar enable — "do you have changes to save?")
  const dirty = useMemo(() => computeDirty(rows, localQuantities), [rows, localQuantities])

  // Menu productIds as a Set for client-side out-of-menu guard before send
  const menuProductIds = useMemo(() => new Set(rows.map((row) => row.product.id)), [rows])

  // Would saving, with nothing edited, change the stored order? That is what the
  // notice claims, and computeDirty cannot answer it — it compares the local
  // edits against these same floored rows and sees no change.
  const cartaChanged = useMemo(
    () => divergesFromStored(rows, order, menuProductIds, minQuantityPerProduct),
    [rows, order, menuProductIds, minQuantityPerProduct],
  )

  const refetch = useCallback(async () => {
    await Promise.all([
      utils.orders.proposalForWeek.invalidate(),
      // Everything derived from the order goes with it: the store switcher's
      // per-store week badge reads accessibleStoresWeekStatus, so leaving it
      // cached would show a stale state next to a just-saved order.
      utils.orders.accessibleStoresWeekStatus.invalidate(),
    ])
  }, [utils])

  const editLinesMutation = trpc.orders.editLines.useMutation({
    onSuccess: async (_data, variables) => {
      // Advance baseline to the saved state BEFORE refetch. This ensures the
      // post-save refetch sees shouldAdoptServer(newBaseline, local) = true
      // and adopts cleanly without raising a false "server changed" notice.
      //
      // The baseline is built from the lines that were actually SENT, not from
      // the current local state: if an edit landed after the request went out,
      // anchoring to local would make the incoming server payload look already
      // reconciled and silently overwrite that newer edit.
      lastSeededKeyRef.current = '' // force the effect to adopt on refetch
      lastSeededRowsRef.current = new Map(
        variables.lines.map((line) => [line.productId, line.quantity]),
      )
      setMutationError(null)
      setServerChangedWhileDirty(false)
      await refetch()
    },
    onError: async (err) => {
      setMutationError(mapOrderError(err))
      // Reconcile: refetch so local state reflects actual server state.
      // The post-error refetch may or may not change server data; the effect
      // will adopt if no user edits vs baseline, or preserve if edits remain.
      await refetch()
    },
  })

  const onQuantityChange = useCallback((productId: number, quantity: number) => {
    setLocalQuantities((prev) => {
      const next = new Map(prev)
      next.set(productId, quantity)
      return next
    })
    // Clear stale error when user starts editing again
    setMutationError(null)
  }, [])

  const onSave = useCallback(() => {
    if (!order || !isEditable) return
    const lines = buildLines(rows, localQuantities, menuProductIds, minQuantityPerProduct)
    editLinesMutation.mutate({storeId, orderId: order.id, lines})
  }, [order, isEditable, rows, localQuantities, menuProductIds, editLinesMutation, storeId, minQuantityPerProduct])

  const onDiscard = useCallback(() => {
    // Re-seed from current server data; update BOTH refs so the next refetch
    // compares against the freshly seeded baseline.
    const seeded = seedLocalQuantities(rows)
    lastSeededKeyRef.current = serverRowsKey
    lastSeededRowsRef.current = seeded
    setLocalQuantities(seeded)
    setServerChangedWhileDirty(false)
    setMutationError(null)
  }, [rows, serverRowsKey])

  return {
    localQuantities,
    dirty,
    serverChangedWhileDirty,
    cartaChanged,
    mutationError,
    isSaving: editLinesMutation.isPending,
    localTotalUnits,
    localTotalRefs,
    onQuantityChange,
    onSave,
    onDiscard,
  }
}
